import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import format from 'date-fns/format';

import { links } from '[theme-lib]/links';

import DeliveryInformation from '../containers/DeliveryInformation';
import CheckoutButton from '[theme-lib]/apps/header/containers/CheckoutButton';

const WARN_5_MIN_BEFORE_MILISECONDS = 5 * 60 * 1000;
const WARN_10_MIN_BEFORE_MILISECONDS = 10 * 60 * 1000;
const WARN_10_MIN_AFTER_MILISECONDS = -WARN_10_MIN_BEFORE_MILISECONDS;
const TIME_FORMAT = 'h:mm a';

const maxNumberSetTimeoutCanHandle = 2147483647; // https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
const calculateSafeTimeout = (timeout) => Math.min(maxNumberSetTimeoutCanHandle, Math.max(0, timeout));

const cutOffTimeNotChanged = (response, deliverySlot) =>
  response !== '' && response.cutoffTime === deliverySlot.cutoffTime;
const slotExpirationTimeNotChanged = (response, deliverySlot) =>
  response !== '' && response.expirationTime === deliverySlot.expirationTime;

export default class ShopControl extends Component {
  static propTypes = {
    deliverySlot: PropTypes.object,
    isLoggedIn: PropTypes.bool.isRequired,
    isBasketInEditMode: PropTypes.bool.isRequired,
    setErrorMessage: PropTypes.func.isRequired,
    setSuccessMessage: PropTypes.func.isRequired,
    shopControlsVisibility: PropTypes.string.isRequired,
    fetchDeliverySlot: PropTypes.func.isRequired,
    fetchBasket: PropTypes.func.isRequired,
    fetchUserData: PropTypes.func.isRequired,
    hasOrderBeenCutOff: PropTypes.bool.isRequired,
    shouldShowCheckout: PropTypes.bool.isRequired,
  };

  /**
   * Issue with calculation of time. To be removed after fix on server side, which should return dates with timezones.
   *
   * cutoffTime & expirationTime are dates returned by endpoint in local time, which is BST or GMT timezone.
   *
   * Date.now() is a specific time in user timezone, which may be different to server timezone. To avoid issues
   * connected with it, server should return datetime with timezone. It should be fixed as soon as possible.
   *
   * Currently there is no other way than add timezone information manually.
   */

  constructor(props) {
    super(props);
    this.slotCutOffTimer = null;
    this.slotCutOff10minBeforeTimer = null;
    this.slotExpiration10minBeforeTimer = null;
    this.slotExpiration5minBeforeTimer = null;
    this.slotExpiredTimer = null;
  }

  componentDidMount() {
    const { hasOrderBeenCutOff, setErrorMessage } = this.props;
    if (hasOrderBeenCutOff) {
      setErrorMessage(
        'You didn\'t check out in time so we\'re processing you original order.<br />' +
        'Any changes you have made have been cancelled.',
        true
      );
    }
    this.startTimers();
  }

  shouldComponentUpdate(nextProps) {
    const { deliverySlot, isBasketInEditMode, hasOrderBeenCutOff } = this.props;
    const {
      deliverySlot: newDeliverySlot,
      isBasketInEditMode: newIsBasketInEditMode,
      hasOrderBeenCutOff: newHasOrderBeenCutOff,
    } = nextProps;
    const { cutoffTime, endTime, expirationTime } = deliverySlot || {};
    const { cutoffTime: newCutOffTime, endTime: newEndTime, expirationTime: newExpirationTime } = newDeliverySlot || {};

    return cutoffTime !== newCutOffTime
      || endTime !== newEndTime
      || expirationTime !== newExpirationTime
      || isBasketInEditMode !== newIsBasketInEditMode
      || hasOrderBeenCutOff !== newHasOrderBeenCutOff;
  }

  componentDidUpdate(prevProps) {
    const { hasOrderBeenCutOff, setErrorMessage } = this.props;
    const { hasOrderBeenCutOff: prevHasOrderBeenCutOff } = prevProps;
    if (!prevHasOrderBeenCutOff && hasOrderBeenCutOff) {
      setErrorMessage(
        'You didn\'t check out in time so we\'re processing you original order.<br />' +
        'Any changes you have made have been cancelled.',
        true
      );
    }
    this.cancelAllTimers();
    this.startTimers();
  }

  componentWillUnmount() {
    this.cancelAllTimers();
  }

  cancelAllTimers() {
    clearTimeout(this.slotExpiration10minBeforeTimer);
    clearTimeout(this.slotExpiration5minBeforeTimer);
    clearTimeout(this.slotExpiredTimer);
    clearTimeout(this.slotCutOff10minBeforeTimer);
    clearTimeout(this.slotCutOffTimer);
    clearTimeout(this.hardCutoffPointTimer);
  }

  startTimers() {
    const { deliverySlot, isBasketInEditMode } = this.props;
    const { timeToCutoffInMillis, timeToExpireInMillis } = deliverySlot || {}; // in edit order mode, user can change an order till this time, if not available -
    if (isBasketInEditMode) {
      if (timeToCutoffInMillis >= WARN_10_MIN_AFTER_MILISECONDS) {
        this.startSlotHardCutOffTimer();
      }

      if (timeToCutoffInMillis > 0) {
        this.startSlotCutOffWarningTimer();
      }

      if (timeToCutoffInMillis >= WARN_10_MIN_BEFORE_MILISECONDS) {
        this.start10minTimerBeforeSlotCutOff();
      }
    }

    if (timeToExpireInMillis > 0) {
      this.startSlotExpiredTimer();
    }

    if (timeToExpireInMillis >= WARN_5_MIN_BEFORE_MILISECONDS) {
      this.start5minBeforeSlotExpirationTimer();
    }

    if (timeToExpireInMillis >= WARN_10_MIN_BEFORE_MILISECONDS) {
      this.start10minBeforeSlotExpirationTimer();
    }
  }

  startSlotHardCutOffTimer() {
    // Order is edited - show information about cut-off point expire
    const { fetchDeliverySlot, fetchBasket, fetchUserData, deliverySlot } = this.props;
    const { timeToCutoffInMillis } = deliverySlot || {};
    const hardCutoffPointTimeLeft = calculateSafeTimeout(timeToCutoffInMillis + WARN_10_MIN_BEFORE_MILISECONDS);

    this.hardCutoffPointTimer = setTimeout(() => {
      // fetch basket before fetching user profile to populate user message
      fetchBasket().then(() => {
        fetchUserData();
        fetchDeliverySlot();
      });
    }, hardCutoffPointTimeLeft);
  }

  startSlotCutOffWarningTimer() {
    // Order is edited - show information about cut-off point expire
    clearTimeout(this.slotCutOffTimer);

    const { deliverySlot, setErrorMessage, fetchDeliverySlot } = this.props;
    const { timeToCutoffInMillis } = deliverySlot || {};
    const cutoffPointTimeLeft = calculateSafeTimeout(timeToCutoffInMillis);

    this.slotCutOffTimer = setTimeout(() => {
      fetchDeliverySlot().then((response) => {
        if (cutOffTimeNotChanged(response, deliverySlot)) {
          setErrorMessage('We\'re ready to process your order so please checkout immediately.', true);
        }
      });
    }, cutoffPointTimeLeft);
  }

  start10minTimerBeforeSlotCutOff() {
    clearTimeout(this.slotCutOff10minBeforeTimer);
    const { deliverySlot, fetchDeliverySlot, setSuccessMessage, setErrorMessage } = this.props;
    const startCheckoutNowTime = calculateSafeTimeout(
      deliverySlot.timeToCutoffInMillis - WARN_10_MIN_BEFORE_MILISECONDS
    );
    const { cutoffTime } = deliverySlot;

    this.slotCutOff10minBeforeTimer = setTimeout(() => {
      fetchDeliverySlot().then((response) => {
        if (cutOffTimeNotChanged(response, deliverySlot)) {
          setSuccessMessage(
            `You must checkout your order by <strong>${format(cutoffTime, TIME_FORMAT)}</strong>` +
            ' so we can process changes.',
            true
          );
        }
      }).catch(() => {
        setErrorMessage('Sorry, something went wrong. Please refresh the page.');
      });
    }, startCheckoutNowTime);
  }

  start10minBeforeSlotExpirationTimer() {
    clearTimeout(this.slotExpiration10minBeforeTimer);
    const { deliverySlot, fetchDeliverySlot, setSuccessMessage, setErrorMessage } = this.props;
    const slotExpiration10minWarnTime = calculateSafeTimeout(
      deliverySlot.timeToExpireInMillis - WARN_10_MIN_BEFORE_MILISECONDS
    );

    this.slotExpiration10minBeforeTimer = setTimeout(() => {
      fetchDeliverySlot().then((response) => {
        if (slotExpirationTimeNotChanged(response, deliverySlot)) {
          setSuccessMessage(
            'Your delivery will expire in less than <strong>10 minutes</strong>,' +
            ' please checkout by then to keep your chosen time.',
            true
          );
        }
      }).catch(() => {
        setErrorMessage('Sorry, something went wrong. Please refresh the page.');
      });
    }, slotExpiration10minWarnTime);
  }

  start5minBeforeSlotExpirationTimer() {
    clearTimeout(this.slotExpiration5minBeforeTimer);
    const { setSuccessMessage, setErrorMessage, deliverySlot, fetchDeliverySlot } = this.props;
    const slotExpiration5minWarnTime = calculateSafeTimeout(
      deliverySlot.timeToExpireInMillis - WARN_5_MIN_BEFORE_MILISECONDS
    );

    this.slotExpiration5minBeforeTimer = setTimeout(() => {
      fetchDeliverySlot().then((response) => {
        if (slotExpirationTimeNotChanged(response, deliverySlot)) {
          setSuccessMessage(
            'Your delivery will expire in less than <strong>5 minutes</strong>,' +
            ' please checkout by then to keep your chosen time.',
            true
          );
        }
      }).catch(() => {
        setErrorMessage('Sorry, something went wrong. Please refresh the page.');
      });
    }, slotExpiration5minWarnTime);
  }

  startSlotExpiredTimer() {
    clearTimeout(this.slotExpiredTimer);
    const { deliverySlot, setErrorMessage, fetchDeliverySlot } = this.props;
    const slotExpirationWarnTime = calculateSafeTimeout(deliverySlot.timeToExpireInMillis);

    this.slotExpiredTimer = setTimeout(() => {
      fetchDeliverySlot().then((response) => {
        if (response === '') {
          setErrorMessage(
            'You didn\'t check out in time so your delivery slot has expired.<br />' +
            'Please choose another delivery time.',
            true
          );
        }
      }).catch(() => {
        setErrorMessage('Sorry, something went wrong. Please refresh the page.');
      });
    }, slotExpirationWarnTime);
  }

  render() {
    const { isLoggedIn, shopControlsVisibility, shouldShowCheckout } = this.props;
    const basketUrl = isLoggedIn ? links.DISPLAY_BASKET : links.DISPLAY_LOGGED_OUT_BASKET;

    return (
      <div className="hd-orderOptions">
        <DeliveryInformation />
        <Link
          to={basketUrl}
          className="viewTrolley button"
        >
          View trolley
        </Link>
        { shouldShowCheckout && <CheckoutButton shopControlsVisibility={shopControlsVisibility} />}
      </div>
    );
  }
}
